import type { FetchResult } from '@apollo/client';

import { isDevelopment } from '@constants/env';

import { useSession } from '@hooks/useSession';

import type {
  DeleteCuratorTaskRecordMutation,
  DeleteCuratorTaskRecordMutationVariables,
  GetAllCuratorTaskRecordsForPatientQuery,
  GetAllCuratorTaskRecordsQuery,
  PostCuratorTaskRecordMutation,
  PostCuratorTaskRecordMutationVariables,
  UpdateCuratorTaskRecordMutation,
  UpdateCuratorTaskRecordMutationVariables,
} from 'generated/curator-task.generated';
import {
  GetAllCuratorTaskRecordsDocument,
  GetAllCuratorTaskRecordsForPatientDocument,
  useDeleteCuratorTaskRecordMutation,
  useGetAllCuratorTaskRecordsForPatientQuery,
  useGetAllCuratorTaskRecordsQuery,
  usePostCuratorTaskRecordMutation,
  useUpdateCuratorTaskRecordMutation,
} from 'generated/curator-task.generated';
import { TaskDateFilter, TaskOwnership } from 'generated/types';

export const useGetAllCuratorTasksForUser = () => {
  const {
    state: { isCurator },
  } = useSession();
  const response = useGetAllCuratorTaskRecordsQuery({
    variables: {
      input: {
        filters: {
          ownership: TaskOwnership.MY,
          date_filter: TaskDateFilter.ALL,
        },
      },
    },
    skip: !isCurator,
  });
  return {
    ...response,
    data: response.data?.getAllCuratorTaskRecords,
  };
};

export const useGetAllCuratorTasksForPatient = (params: {
  vitall_patient_id?: string | null;
}) => {
  const {
    state: { isCurator },
  } = useSession();
  const response = useGetAllCuratorTaskRecordsForPatientQuery({
    variables: {
      input: {
        vitall_patient_id: params.vitall_patient_id ?? '',
      },
    },
    skip: !isCurator || !params.vitall_patient_id,
  });
  return {
    ...response,
    data: response.data?.getAllCuratorTaskRecordsForPatient,
  };
};

export function useCreateCuratorTask(): [
  (
    params: PostCuratorTaskRecordMutationVariables
  ) => Promise<FetchResult<PostCuratorTaskRecordMutation>>,
  boolean,
] {
  const {
    state: { activeProfile, loggedInProfile },
  } = useSession();
  const [vitall_patient_id] = [activeProfile?.vitall_patient_id];
  const [mutate, { loading: baseLoading }] = usePostCuratorTaskRecordMutation();
  const loading = baseLoading;

  async function createCuratorTask(
    params: PostCuratorTaskRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.postCuratorTaskRecord) {
          return;
        }
        try {
          if (
            loggedInProfile?.user_id === response.postCuratorTaskRecord.user_id
          ) {
            cache.updateQuery<GetAllCuratorTaskRecordsQuery>(
              {
                query: GetAllCuratorTaskRecordsDocument,
                variables: {
                  filters: {
                    date_filter: 'ALL',
                    owbership: 'MY',
                  },
                },
              },
              (data) => ({
                getAllCuratorTaskRecords: {
                  success: true,
                  message: 'success',
                  data: [
                    ...(data?.getAllCuratorTaskRecords.data ?? []),
                    response.postCuratorTaskRecord,
                  ],
                },
              })
            );
          }

          if (
            vitall_patient_id ===
            response.postCuratorTaskRecord.vitall_patient_id
          ) {
            cache.updateQuery<GetAllCuratorTaskRecordsForPatientQuery>(
              {
                query: GetAllCuratorTaskRecordsForPatientDocument,
                variables: {
                  vitall_patient_id,
                },
              },
              (data) => ({
                getAllCuratorTaskRecordsForPatient: {
                  success: true,
                  message: 'success',
                  data: [
                    ...(data?.getAllCuratorTaskRecordsForPatient.data ?? []),
                    response.postCuratorTaskRecord,
                  ],
                },
              })
            );
          }
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    return response;
  }
  return [createCuratorTask, loading];
}

export function useUpdateCuratorTask(): [
  (
    params: UpdateCuratorTaskRecordMutationVariables
  ) => Promise<FetchResult<UpdateCuratorTaskRecordMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    useUpdateCuratorTaskRecordMutation();
  const loading = baseLoading;

  async function updateCuratorTask(
    params: UpdateCuratorTaskRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
    });
    return response;
  }
  return [updateCuratorTask, loading];
}

export function useDeleteCuratorTask(): [
  (
    params: DeleteCuratorTaskRecordMutationVariables
  ) => Promise<FetchResult<DeleteCuratorTaskRecordMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    useDeleteCuratorTaskRecordMutation();
  const loading = baseLoading;

  async function deleteCuratorTask(
    params: DeleteCuratorTaskRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.deleteCuratorTaskRecord) {
          return;
        }
        const { curator_task_id: id } = params.input;
        try {
          const normalizedId = cache.identify({
            curator_task_id: id,
            __typename: 'CuratorTaskModel',
          });
          cache.evict({ id: normalizedId });
          cache.gc();
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    return response;
  }
  return [deleteCuratorTask, loading];
}
